import React, { useRef, useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';

const TestimonialsSection = () => {
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const testimonials = [
    {
      name: 'Olivia Bennett',
      company: 'Founder, Bloom & Harbor Skincare',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'Global Trademark Filer made the entire trademark process incredibly smooth. Everything was handled professionally, and I felt supported from start to finish.'
    },
    {
      name: 'Ethan Walker',
      company: 'CEO, VertexWave Technologies',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'Their AI search system uncovered conflicts I would have missed completely. The legal team was responsive and guided me at every step.'
    },
    {
      name: 'Sophia Reynolds',
      company: 'Owner, Daily Grind Café',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'This was one of the best decisions for my business. Knowing my brand is protected gives me complete confidence moving forward.'
    },
    {
      name: 'Liam Carter',
      company: 'Founder, NorthPeak Apparel',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'Fast, efficient, and very professional service. They explained everything clearly and made the filing process stress-free.'
    },
    {
      name: 'Isabella Moore',
      company: 'Co-Founder, BrightNest Interiors',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'I was impressed by how detailed their trademark search was. It saved me from making a costly mistake.'
    },
    {
      name: 'Noah Thompson',
      company: 'CEO, Apex Digital Labs',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'The support team stayed in touch throughout the entire process. I never felt lost or confused at any stage.'
    },
    {
      name: 'Ava Johnson',
      company: 'Owner, SweetSprout Bakery',
      image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'They handled everything from search to filing with great accuracy. The process was much easier than I expected.'
    },
    {
      name: 'James Anderson',
      company: 'Founder, BlueRiver Fitness',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'Their attorneys were knowledgeable and quick to respond. I felt my brand was in safe hands.'
    },
    {
      name: 'Mia Robinson',
      company: 'CEO, UrbanEdge Consulting',
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'Affordable, transparent, and highly professional service. I would definitely recommend them to any business owner.'
    },
    {
      name: 'Benjamin Hughes',
      company: 'Owner, SilverLine Media',
      image: 'https://images.unsplash.com/photo-1507591064344-4c6ce005b968?q=80&w=200&auto=format&fit=crop',
      rating: 5,
      text: 'Excellent experience from start to finish. The entire trademark process was handled with care and precision.'
    }
  ];

  const scrollToIndex = (index) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 420; // Card width + gap
      scrollContainerRef.current.scrollTo({
        left: scrollAmount * index,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  // Update current index on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollAmount = 420;
      const newIndex = Math.round(container.scrollLeft / scrollAmount);
      setCurrentIndex(newIndex);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Trusted by Thousands of Businesses
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See what clients are saying about their experience with Global Trademark Filer
          </p>
        </div>

        {/* Horizontal Scrolling Container */}
        <div className="relative">
          {/* Scrollable Container */}
          <div 
            ref={scrollContainerRef}
            className={`flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', userSelect: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 flex-shrink-0 w-[400px] snap-center"
              >
                <Quote className="w-10 h-10 text-[#168356] mb-4" />
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#168356] text-[#168356]" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-black">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dot Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentIndex === index 
                    ? 'bg-[#d29b00] w-8' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;